import React, { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import {
  STAKING_ADDRESS, STAKING_ABI,
  BUYBACK_ADDRESS, BUYBACK_ABI,
  PALPHA_ADDRESS,  PALPHA_ABI,
  DAO_ADDRESS,     DAO_ABI,
} from "../config";
import SwarmPanel from "../components/SwarmPanel";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(wei, decimals = 18) {
  if (wei === null || wei === undefined) return "—";
  try {
    return parseFloat(ethers.formatUnits(wei, decimals)).toLocaleString(undefined, { maximumFractionDigits: 2 });
  } catch { return "—"; }
}

function shortAddr(addr) {
  if (!addr) return "—";
  return addr.slice(0, 6) + "..." + addr.slice(-4);
}

function timeLeft(deadline) {
  const now  = Math.floor(Date.now() / 1000);
  const diff = Number(deadline) - now;
  if (diff <= 0) return "Closed";
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  return h + "h " + m + "m left";
}

// ── Shared card wrapper ───────────────────────────────────────────────────────

function SectionCard({ children, accent = false }) {
  return (
    <div className={`bg-surface border border-gray-800 p-6 ${accent ? "border-t-2 border-t-accent" : ""}`}>
      {children}
    </div>
  );
}

// ── Staking ───────────────────────────────────────────────────────────────────

function StakingSection({ provider, signer, address }) {
  const [data,   setData]   = useState({ staked: null, earned: null, total: null });
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    if (!provider || !address) {
      // Demo fallback when no wallet connected
      setData({
        staked: ethers.parseEther("25000"),
        earned: ethers.parseEther("1250"),
        total:  ethers.parseEther("450000"),
        isDemo: true,
      });
      return;
    }
    try {
      const staking = new ethers.Contract(STAKING_ADDRESS, STAKING_ABI, provider);
      const [staked, earned, total] = await Promise.all([
        staking.stakedBalance(address),
        staking.earned(address),
        staking.totalStaked(),
      ]);
      setData({ staked, earned, total });
    } catch (e) {
      console.error("Staking load:", e);
      setData({
        staked: ethers.parseEther("25000"),
        earned: ethers.parseEther("1250"),
        total:  ethers.parseEther("450000"),
        isDemo: true,
      });
    }
  }, [provider, address]);

  useEffect(() => { load(); }, [load]);

  async function approve(amountWei) {
    const palpha = new ethers.Contract(PALPHA_ADDRESS, PALPHA_ABI, signer);
    await (await palpha.approve(STAKING_ADDRESS, amountWei)).wait();
  }

  async function handleStake() {
    try {
      setStatus("Approving...");
      const amountWei = ethers.parseEther(amount);
      await approve(amountWei);
      setStatus("Staking...");
      const staking = new ethers.Contract(STAKING_ADDRESS, STAKING_ABI, signer);
      await (await staking.stake(amountWei)).wait();
      setStatus("Staked!"); setAmount(""); load();
    } catch (e) { setStatus("Error: " + (e.reason || e.message)); }
  }

  async function handleUnstake() {
    try {
      setStatus("Unstaking...");
      const amountWei = ethers.parseEther(amount);
      const staking = new ethers.Contract(STAKING_ADDRESS, STAKING_ABI, signer);
      await (await staking.unstake(amountWei)).wait();
      setStatus("Unstaked!"); setAmount(""); load();
    } catch (e) { setStatus("Error: " + (e.reason || e.message)); }
  }

  async function handleClaim() {
    try {
      setStatus("Claiming...");
      const staking = new ethers.Contract(STAKING_ADDRESS, STAKING_ABI, signer);
      await (await staking.claimReward()).wait();
      setStatus("Claimed!"); load();
    } catch (e) { setStatus("Error: " + (e.reason || e.message)); }
  }

  return (
    <SectionCard accent>
      <div className="text-xs font-mono text-gray-600 mb-3 uppercase tracking-wider">// palpha_staking</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {[
          { label: "Your Staked",       value: fmt(data.staked) + " PALPHA" },
          { label: "Earned Rewards",    value: fmt(data.earned) + " PALPHA" },
          { label: "Total Pool Staked", value: fmt(data.total)  + " PALPHA" },
        ].map(({ label, value }) => (
          <div key={label} className="bg-dark border border-gray-800 p-4 edge-glow hover:bg-surface-hover transition-all">
            <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-1">{label}</div>
            <div className="text-lg font-mono font-semibold text-white">{value}</div>
          </div>
        ))}
      </div>

      {signer ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="bg-dark border border-gray-700 text-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-accent flex-1 min-w-[160px]"
            type="number"
            placeholder="Amount (PALPHA)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <button
            className="bg-accent hover:bg-accent-dark text-black px-4 py-2 text-sm font-bold transition-colors"
            onClick={handleStake}
          >
            Stake
          </button>
          <button
            className="border border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white px-4 py-2 text-sm font-bold transition-colors"
            onClick={handleUnstake}
          >
            Unstake
          </button>
          <button
            className="border border-success/40 text-success hover:bg-success/10 px-4 py-2 text-sm font-bold transition-colors"
            onClick={handleClaim}
          >
            Claim Rewards
          </button>
        </div>
      ) : (
        <p className="text-sm text-gray-500 font-mono">// connect wallet to stake</p>
      )}
      {status && <p className="mt-3 text-sm font-mono text-accent">{status}</p>}
    </SectionCard>
  );
}

// ── Governance ────────────────────────────────────────────────────────────────

const MOCK_PROPOSALS = [
  {
    id: 0,
    proposer:       "0xfEe9eeC07f9f689aA2F81e240f3199E02f93896d",
    description:    "Increase vault max drawdown circuit breaker from 20% to 25% to allow longer trend trades.",
    forVotes:       ethers.parseEther("320000"),
    againstVotes:   ethers.parseEther("80000"),
    executed:       true,
    canceled:       false,
    votingDeadline: BigInt(Math.floor(Date.now() / 1000) - 86400),
  },
  {
    id: 1,
    proposer:       "0xfEe9eeC07f9f689aA2F81e240f3199E02f93896d",
    description:    "Allocate 5% of buyback revenue to a community grants multisig for ecosystem development.",
    forVotes:       ethers.parseEther("150000"),
    againstVotes:   ethers.parseEther("20000"),
    executed:       false,
    canceled:       false,
    votingDeadline: BigInt(Math.floor(Date.now() / 1000) + 3600 * 36),
  },
];

function GovernanceSection({ provider, signer, address }) {
  const [proposals,      setProposals]     = useState(MOCK_PROPOSALS);
  const [usingCachedData, setUsingCached]  = useState(true);
  const [loading,        setLoading]       = useState(false);
  const [liveCount,      setLiveCount]     = useState(null);
  const [showForm,       setShowForm]      = useState(false);
  const [form,           setForm]          = useState({ description: "", target: "", callData: "0x" });
  const [status,         setStatus]        = useState("");

  const fetchProposals = useCallback(async () => {
    if (!provider) return;
    setLoading(true);
    try {
      const dao   = new ethers.Contract(DAO_ADDRESS, DAO_ABI, provider);
      const count = await dao.proposalCount();
      const n     = Number(count);
      setLiveCount(n);
      if (n === 0) { setProposals(MOCK_PROPOSALS); setUsingCached(true); return; }
      const fetched = await Promise.all(
        Array.from({ length: n }, (_, i) =>
          dao.getProposal(i).then((p) => ({
            id: i, proposer: p[0], description: p[1], targetContract: p[2],
            createdAt: p[3], votingDeadline: p[4],
            forVotes: p[5], againstVotes: p[6], executed: p[7], canceled: p[8],
          }))
        )
      );
      setProposals(fetched);
      setUsingCached(false);
    } catch (e) {
      console.error("fetchProposals failed:", e);
      setProposals(MOCK_PROPOSALS);
      setUsingCached(true);
    } finally { setLoading(false); }
  }, [provider]);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  async function handleCreate() {
    if (!signer) return;
    try {
      setStatus("Creating proposal...");
      const dao          = new ethers.Contract(DAO_ADDRESS, DAO_ABI, signer);
      const callDataHex  = form.callData.startsWith("0x") ? form.callData : "0x" + form.callData;
      await (await dao.createProposal(form.description, form.target, callDataHex)).wait();
      setStatus("Proposal created on-chain!"); setShowForm(false);
    } catch (e) { setStatus("Error: " + (e.reason || e.message)); }
  }

  async function handleVote(id, support) {
    if (!signer) return;
    try {
      setStatus("Voting...");
      const dao = new ethers.Contract(DAO_ADDRESS, DAO_ABI, signer);
      await (await dao.vote(id, support)).wait();
      setStatus("Vote cast!");
    } catch (e) { setStatus("Error: " + (e.reason || e.message)); }
  }

  return (
    <SectionCard accent>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-gray-600 uppercase tracking-wider">// dao_governance</div>
        <div className="flex items-center gap-2 flex-wrap">
          {liveCount !== null && (
            <span className="text-xs font-mono bg-gray-800 text-gray-400 px-2 py-1 rounded">
              {liveCount} on-chain proposal{liveCount !== 1 ? "s" : ""}
            </span>
          )}
          {usingCachedData && (
            <span className="text-xs font-mono bg-orange-900/40 text-warning border border-warning/30 px-2 py-1 rounded">
              ⚠ Cached data
            </span>
          )}
          <button
            className="border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 px-3 py-1 rounded text-xs font-mono transition-colors disabled:opacity-40"
            onClick={fetchProposals}
            disabled={loading || !provider}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
          {signer && (
            <button
              className="bg-accent hover:bg-accent-dark text-black px-3 py-1 rounded text-xs font-semibold transition-colors"
              onClick={() => setShowForm(!showForm)}
            >
              {showForm ? "Cancel" : "+ New Proposal"}
            </button>
          )}
        </div>
      </div>

      {/* New proposal form */}
      {showForm && (
        <div className="bg-dark border border-gray-800 rounded-lg p-4 mb-4 flex flex-col gap-3">
          <textarea
            className="bg-surface border border-gray-700 text-gray-200 px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-accent resize-y min-h-[80px] w-full"
            placeholder="Proposal description (be concise and specific)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <input
            className="bg-surface border border-gray-700 text-gray-200 px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-accent"
            placeholder="Target contract address (0x...)"
            value={form.target}
            onChange={(e) => setForm({ ...form, target: e.target.value })}
          />
          <input
            className="bg-surface border border-gray-700 text-gray-200 px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-accent"
            placeholder="Call data (0x for no-op)"
            value={form.callData}
            onChange={(e) => setForm({ ...form, callData: e.target.value })}
          />
          <button
            className="bg-accent hover:bg-accent-dark text-black px-4 py-2 rounded-lg text-sm font-semibold transition-colors self-start"
            onClick={handleCreate}
          >
            Submit On-Chain
          </button>
        </div>
      )}

      {/* Proposal terminal log */}
      <div className="bg-dark border border-gray-800 font-mono text-xs">
        <div className="px-4 py-2 border-b border-gray-800 text-gray-600 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-accent inline-block" />
          <span>// PolyAlphaDAO on-chain proposals ({proposals.length} found)</span>
        </div>
        {proposals.map((p) => {
          const total    = p.forVotes + p.againstVotes;
          const forPct   = total > 0n ? Number((p.forVotes * 100n) / total) : 0;
          const isActive = !p.executed && !p.canceled && Number(p.votingDeadline) > Date.now() / 1000;
          const statusTag = p.executed ? "[EXECUTED]" : p.canceled ? "[CANCELED]" : isActive ? "[ACTIVE]" : "[CLOSED]";
          const statusCls = p.executed ? "text-success" : p.canceled ? "text-red-400" : isActive ? "text-accent" : "text-gray-500";

          return (
            <div key={p.id} className="border-b border-gray-800/50 px-4 py-3 last:border-0 hover:bg-surface-hover transition-colors">
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <span className="text-gray-600">$</span>
                <span className="text-white text-sm">prop-{String(p.id + 1).padStart(3, "0")}</span>
                <span className={statusCls}>{statusTag}</span>
                <span className="text-gray-600 ml-auto">{timeLeft(p.votingDeadline)} · by {shortAddr(p.proposer)}</span>
              </div>
              <p className="text-gray-400 pl-4 mb-2 leading-relaxed">{p.description}</p>
              <div className="pl-4 flex gap-4 mb-2">
                <span className="text-success">YES: {fmt(p.forVotes)} ({forPct}%)</span>
                <span className="text-red-400">NO: {fmt(p.againstVotes)}</span>
              </div>
              <div className="pl-4 h-1 bg-gray-800 w-48 overflow-hidden mb-2">
                <div className="h-full bg-accent transition-all duration-500" style={{ width: forPct + "%" }} />
              </div>
              {isActive && signer && (
                <div className="flex gap-2 mt-2 pl-4">
                  <button
                    className="bg-accent hover:bg-accent-dark text-black px-3 py-1 text-xs font-semibold transition-colors"
                    onClick={() => handleVote(p.id, true)}
                  >
                    vote YES
                  </button>
                  <button
                    className="border border-red-500/40 text-red-400 hover:bg-red-950 px-3 py-1 text-xs font-semibold transition-colors"
                    onClick={() => handleVote(p.id, false)}
                  >
                    vote NO
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {status && <p className="mt-3 text-sm font-mono text-accent">{status}</p>}
    </SectionCard>
  );
}

// ── Buyback & Burn ────────────────────────────────────────────────────────────

function BuybackSection({ provider }) {
  const [data, setData] = useState({ burned: null, pending: null });

  useEffect(() => {
    if (!provider) {
      // Demo fallback
      setData({
        burned:  ethers.parseEther("82500"),
        pending: ethers.parseEther("12300"),
        isDemo:  true,
      });
      return;
    }
    const buyback = new ethers.Contract(BUYBACK_ADDRESS, BUYBACK_ABI, provider);
    Promise.all([buyback.totalAlphaBurned(), buyback.pendingBurn()])
      .then(([burned, pending]) => setData({ burned, pending }))
      .catch((e) => {
        console.error("Buyback load:", e);
        setData({
          burned:  ethers.parseEther("82500"),
          pending: ethers.parseEther("12300"),
          isDemo:  true,
        });
      });
  }, [provider]);

  return (
    <SectionCard accent>
      <div className="text-xs font-mono text-gray-600 mb-3 uppercase tracking-wider">// buyback_and_burn</div>
      <p className="text-sm text-gray-500 font-mono mb-4">
        Trading profits buy PALPHA on the open market and burn it, reducing total supply.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <div className="bg-dark border border-accent/20 p-4 edge-glow hover:bg-surface-hover transition-all">
          <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-1">Total PALPHA Burned</div>
          <div className="text-2xl font-mono font-bold text-white">{fmt(data.burned)} PALPHA</div>
        </div>
        <div className="bg-dark border border-gray-800 p-4 edge-glow hover:bg-surface-hover transition-all">
          <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-1">Pending Burn Queue</div>
          <div className="text-2xl font-mono font-bold text-white">{fmt(data.pending)} PALPHA</div>
        </div>
      </div>
      <div className="space-y-2">
        {[
          ["Buyback Contract", BUYBACK_ADDRESS],
          ["DAO Contract",     DAO_ADDRESS],
        ].map(([label, addr]) => (
          <div key={label} className="flex items-center justify-between py-2.5 border-b border-gray-800/60 text-sm">
            <span className="text-gray-400">{label}</span>
            <a
              href={"https://testnet.chainlab.fun/address/" + addr}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs text-accent hover:underline"
            >
              {shortAddr(addr)}
            </a>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const SECTION_TABS = [
  { id: "staking",    label: "Staking"        },
  { id: "governance", label: "Governance"     },
  { id: "buyback",    label: "Buyback & Burn" },
  { id: "swarm",      label: "MiroFish Swarm" },
];

export default function PALPHAHubPage() {
  const [activeSection, setActiveSection] = useState("staking");
  const [provider,      setProvider]      = useState(null);
  const [signer,        setSigner]        = useState(null);
  const [address,       setAddress]       = useState(null);
  const [walletStatus,  setWalletStatus]  = useState("disconnected");

  async function connectWallet() {
    if (!window.ethereum) { alert("No wallet detected. Install MetaMask."); return; }
    try {
      setWalletStatus("connecting");
      const p    = new ethers.BrowserProvider(window.ethereum);
      await p.send("eth_requestAccounts", []);
      const s    = await p.getSigner();
      const addr = await s.getAddress();
      setProvider(p); setSigner(s); setAddress(addr); setWalletStatus("connected");
    } catch (e) {
      setWalletStatus("disconnected");
      console.error("Wallet connect:", e);
    }
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">PALPHA Hub</h1>
          <p className="text-gray-500 font-mono text-xs mt-1">
            stake · govern · buyback · swarm_intelligence
          </p>
        </div>
        <div>
          {walletStatus === "connected" ? (
            <span className="flex items-center gap-2 bg-dark border border-gray-700 px-3 py-2 font-mono text-sm text-accent">
              <span className="w-2 h-2 bg-success shadow-[0_0_5px_#00E676]" />
              {shortAddr(address)}
            </span>
          ) : (
            <button
              className="bg-accent hover:bg-accent-dark text-black px-4 py-2 text-sm font-bold transition-colors"
              onClick={connectWallet}
            >
              {walletStatus === "connecting" ? "Connecting..." : "Connect Wallet"}
            </button>
          )}
        </div>
      </div>

      {/* Terminal tab navigation */}
      <div className="flex mb-6 border border-gray-800 w-fit overflow-hidden font-mono">
        {SECTION_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveSection(t.id)}
            className={`px-4 py-2 text-sm font-mono transition-colors border-r border-gray-800 last:border-r-0 ${
              activeSection === t.id
                ? "bg-accent text-black font-bold"
                : "bg-dark text-gray-400 hover:text-accent"
            }`}
          >
            ./{t.id}
          </button>
        ))}
      </div>

      {activeSection === "staking"    && <StakingSection    provider={provider} signer={signer} address={address} />}
      {activeSection === "governance" && <GovernanceSection provider={provider} signer={signer} address={address} />}
      {activeSection === "buyback"    && <BuybackSection    provider={provider} />}
      {activeSection === "swarm"      && <SwarmPanel />}
    </div>
  );
}
